import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  BranchOfferingsResponse,
  PatientSummaryResponse,
  RecordSaleRequest,
  SaleResponse,
  WalletResponse,
} from '../../api/types';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/states/StateViews';
import { useAuthStore } from '../../auth/authStore';
import { useLocalisedName } from '../../i18n/localisedName';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { money } from '../catalog/money';
import { useOpenReceipt } from './useOpenReceipt';

/**
 * Reception's checkout (FR-403, FR-901, RULE-013).
 *
 * The screen the paper notebook is replaced by, so it is built around the order the desk actually
 * works in: find the patient, add what they are buying, take the money, hand over the receipt.
 *
 * Two things it deliberately does not do. It never sends a price — the basket says what to sell and
 * the server prices it against the clinic's own catalogue, so a tampered client cannot change what
 * anything costs. And it does not let a discount above the cap through quietly: the server refuses,
 * and the refusal is shown as the sentence the receptionist needs ("ask a manager"), not as an
 * error code.
 */
interface BasketLine {
  readonly key: string;
  readonly label: string;
  readonly serviceId?: string;
  readonly packageProductId?: string;
  readonly unitPrice: string;
  quantity: number;
  discount: string;
}

export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branchId, branches, choose } = useBranchPicker();
  const canRecord = useAuthStore((state) => state.permissions).has('SALE_RECORD');

  const [search, setSearch] = useState('');
  const [patient, setPatient] = useState<PatientSummaryResponse | null>(null);
  const [basket, setBasket] = useState<readonly BasketLine[]>([]);
  const [method, setMethod] = useState<'CASH' | 'INSTAPAY' | 'CARD_ON_POS'>('CASH');
  const [reference, setReference] = useState('');
  const [paying, setPaying] = useState(true);
  const [approval, setApproval] = useState('');
  const [receipt, setReceipt] = useState<SaleResponse | null>(null);

  const patients = useApiQuery<PatientSummaryResponse[]>(
    queryKeys.patients(clinicId ?? '', search),
    `/clinics/${clinicId}/patients?query=${encodeURIComponent(search)}`,
    { enabled: Boolean(clinicId) && search.trim().length >= 2 },
  );

  const offerings = useApiQuery<BranchOfferingsResponse>(
    queryKeys.offerings(clinicId ?? '', branchId ?? ''),
    `/clinics/${clinicId}/catalog/offerings?branchId=${branchId}`,
    { enabled: Boolean(clinicId && branchId) },
  );

  const wallet = useApiQuery<WalletResponse>(
    queryKeys.wallet(clinicId ?? '', patient?.id ?? ''),
    `/clinics/${clinicId}/patients/${patient?.id}/wallet`,
    { enabled: Boolean(clinicId && patient?.id) },
  );

  const sell = useApiMutation<SaleResponse, RecordSaleRequest>(
    (body) => ({ path: `/clinics/${clinicId}/sales`, options: { method: 'POST', body } }),
    {
      invalidates: [
        queryKeys.wallet(clinicId ?? '', patient?.id ?? ''),
        queryKeys.sales(clinicId ?? '', branchId ?? ''),
      ],
    },
  );

  /**
   * Paying happens after selling, not with it.
   *
   * The basket deliberately does not know what it costs — the server prices it — so the amount to
   * take is only knowable once the sale exists. Two calls, and the second one uses the gross the
   * server just computed rather than a total this screen added up.
   */
  const pay = useApiMutation<SaleResponse, { readonly saleId: string; readonly amount: string }>(
    ({ saleId, amount }) => ({
      path: `/clinics/${clinicId}/sales/${saleId}/payments`,
      options: {
        method: 'POST',
        body: { method, amount: { amount, currency: 'EGP' }, reference: reference || undefined },
      },
    }),
    { invalidates: [queryKeys.sales(clinicId ?? '', branchId ?? '')], onSuccess: () => setReference('') },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.checkout')} description={t('home.noClinic')} />;
  }

  const total = basket.reduce(
    (running, line) => running + Number(line.unitPrice) * line.quantity - Number(line.discount || '0'),
    0,
  );

  const add = (line: Omit<BasketLine, 'quantity' | 'discount' | 'key'>) =>
    setBasket((current) => [
      ...current,
      { ...line, key: `${line.serviceId ?? line.packageProductId}-${current.length}`, quantity: 1, discount: '' },
    ]);

  const submit = async () => {
    if (!patient || !branchId || basket.length === 0) {
      return;
    }
    const sale = await sell.mutateAsync({
      branchId,
      patientMembershipId: patient.id ?? '',
      locale: i18n.language,
      lines: basket.map((line) => ({
        serviceId: line.serviceId,
        packageProductId: line.packageProductId,
        quantity: line.quantity,
        manualDiscount: line.discount ? { amount: Number(line.discount).toFixed(2), currency: 'EGP' } : undefined,
      })),
      discountApproval: approval
        ? { approvedByAccountId: approval, reason: t('checkout.approvedAtDesk') }
        : undefined,
    });

    const settled = paying && sale.gross?.amount
      ? await pay.mutateAsync({ saleId: sale.id ?? '', amount: sale.gross.amount })
      : sale;

    setReceipt(settled);
    setBasket([]);
    setApproval('');
  };

  return (
    <>
      <PageHeader title={t('nav.items.checkout')} description={t('checkout.description')} />

      <div className="panel">
        <label className="field">
          <span>{t('board.branch')}</span>
          <select value={branchId ?? ''} onChange={(event) => choose(event.target.value)}>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {name(branch, branch.code)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* ── Who is paying ─────────────────────────────────────────────── */}
      <section className="panel">
        <h2>{t('checkout.patient')}</h2>
        {patient ? (
          <p className="chosen">
            <strong>{patient.fullName}</strong> · {patient.phone}{' '}
            <button type="button" className="link" onClick={() => setPatient(null)}>
              {t('common.change')}
            </button>
          </p>
        ) : (
          <>
            <label className="field">
              <span>{t('patients.search')}</span>
              <input value={search} onChange={(event) => setSearch(event.target.value)} />
            </label>
            <ul className="results">
              {(patients.data ?? []).map((row) => (
                <li key={row.id}>
                  <button type="button" onClick={() => setPatient(row)}>
                    {row.fullName} · {row.phone}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {patient && wallet.data ? (
          <p className="wallet-chips">
            {(wallet.data.balances ?? []).map((balance) => (
              <span key={balance.kind} className="chip">
                {t(`checkout.kind.${balance.kind}`, { count: balance.quantity })}
              </span>
            ))}
          </p>
        ) : null}
      </section>

      {/* ── What they are buying ──────────────────────────────────────── */}
      <section className="panel">
        <h2>{t('checkout.basket')}</h2>
        <div className="catalogue-picker">
          {(offerings.data?.services ?? []).map((service) => {
            // Priced by pulse (CR-21), the line's quantity is the pulses fired and its unit one pulse.
            const perPulse = service.pricingMode === 'PULSE';
            const unit = perPulse ? service.pulsePrice : service.price;
            return (
              <button
                key={service.serviceId}
                type="button"
                onClick={() =>
                  add({ label: service.name ?? '', serviceId: service.serviceId, unitPrice: unit?.amount ?? '0' })
                }
              >
                {service.name} ·{' '}
                {perPulse ? t('catalog.perPulse', { price: money(unit) ?? '' }) : money(unit)}
              </button>
            );
          })}
          {(offerings.data?.packages ?? []).map((product) => (
            <button
              key={product.packageId}
              type="button"
              className="package"
              onClick={() =>
                add({
                  label: product.name ?? '',
                  packageProductId: product.packageId,
                  unitPrice: product.price?.amount ?? '0',
                })
              }
            >
              {product.name} · {money(product.price)}
            </button>
          ))}
        </div>

        {basket.length === 0 ? (
          <p className="muted">{t('checkout.emptyBasket')}</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('checkout.item')}</th>
                  <th>{t('checkout.quantity')}</th>
                  <th>{t('checkout.unit')}</th>
                  <th>{t('checkout.discount')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {basket.map((line, index) => (
                  <tr key={line.key}>
                    <td>{line.label}</td>
                    <td>
                      <input
                        type="number"
                        min={1}
                        value={line.quantity}
                        onChange={(event) =>
                          setBasket((current) =>
                            current.map((row, position) =>
                              position === index ? { ...row, quantity: Number(event.target.value) } : row,
                            ),
                          )
                        }
                      />
                    </td>
                    <td>{line.unitPrice}</td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={line.discount}
                        onChange={(event) =>
                          setBasket((current) =>
                            current.map((row, position) =>
                              position === index ? { ...row, discount: event.target.value } : row,
                            ),
                          )
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => setBasket((current) => current.filter((_, position) => position !== index))}
                      >
                        {t('common.remove')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="muted">{t('checkout.netHint', { total: total.toFixed(2) })}</p>
      </section>

      {/* ── Taking the money ──────────────────────────────────────────── */}
      <section className="panel">
        <h2>{t('checkout.payment')}</h2>
        <label className="field checkbox">
          <input type="checkbox" checked={paying} onChange={(event) => setPaying(event.target.checked)} />
          <span>{t('checkout.takePaymentNow')}</span>
        </label>
        {paying ? (
          <div className="form-grid">
            <label className="field">
              <span>{t('checkout.method')}</span>
              <select value={method} onChange={(event) => setMethod(event.target.value as typeof method)}>
                <option value="CASH">{t('checkout.methods.CASH')}</option>
                <option value="INSTAPAY">{t('checkout.methods.INSTAPAY')}</option>
                <option value="CARD_ON_POS">{t('checkout.methods.CARD_ON_POS')}</option>
              </select>
            </label>
            <label className="field">
              <span>{t('checkout.reference')}</span>
              <input value={reference} onChange={(event) => setReference(event.target.value)} />
            </label>
          </div>
        ) : null}

        <label className="field">
          <span>{t('checkout.approval')}</span>
          <input
            value={approval}
            onChange={(event) => setApproval(event.target.value)}
            placeholder={t('checkout.approvalHint')}
          />
        </label>

        {sell.isError ? <ErrorState error={sell.error} /> : null}
        {pay.isError ? <ErrorState error={pay.error} /> : null}

        <button
          type="button"
          className="primary"
          disabled={!canRecord || !patient || basket.length === 0 || sell.isPending || pay.isPending}
          onClick={() => void submit()}
        >
          {t('checkout.complete')}
        </button>
      </section>

      {/* ── The receipt ───────────────────────────────────────────────── */}
      {receipt ? <Receipt sale={receipt} clinicId={clinicId} onClear={() => setReceipt(null)} /> : null}
    </>
  );
}

/**
 * What the desk does once the sale exists: read the total back, and open the receipt.
 *
 * The receipt is a server-rendered HTML document (FR-901) opened in a new tab, so printing is the
 * browser's print dialog — nothing needs building here. It is fetched with the session's token
 * first (see useOpenReceipt), because a plain link would open a tab the API refuses.
 */
function Receipt({
  sale,
  clinicId,
  onClear,
}: {
  readonly sale: SaleResponse;
  readonly clinicId: string;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation();
  const receipt = useOpenReceipt(clinicId);

  return (
    <section className="panel success">
      <h2>{t('checkout.sold', { receipt: sale.receiptNumber })}</h2>
      <dl className="totals">
        <dt>{t('checkout.net')}</dt>
        <dd>{money(sale.net)}</dd>
        <dt>{t('checkout.vat')}</dt>
        <dd>{money(sale.vat)}</dd>
        <dt>{t('checkout.total')}</dt>
        <dd>
          <strong>{money(sale.gross)}</strong>
        </dd>
        <dt>{t('checkout.outstanding')}</dt>
        <dd>{money(sale.outstanding)}</dd>
      </dl>
      <button
        type="button"
        className="primary"
        disabled={receipt.opening !== null}
        onClick={() => void receipt.open(sale.id ?? '', sale.receiptNumber)}
      >
        {t('checkout.openReceipt')}
      </button>
      {receipt.error ? <ErrorState error={receipt.error} /> : null}
      <button type="button" className="link" onClick={onClear}>
        {t('common.dismiss')}
      </button>
    </section>
  );
}
